import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

import { Button } from "@/shared/foundation/primitives";

interface DeleteEntityDialogProps {
  open: boolean;
  entityId: string;
  /** Ids nested under `entityId`, at any depth. Non-empty, or this would not open. */
  descendants: string[];
  /** The parent children would splice up to; `undefined` makes them roots. */
  promotedParent: string | undefined;
  onClose: () => void;
  /** `cascade` removes the whole subtree; otherwise only `entityId` goes. */
  onConfirm: (cascade: boolean) => void;
}

/**
 * Asks what should happen to an entity's children before deleting it.
 *
 * Only shown when the entity has children — with none there is no choice to
 * make, and prompting would just be a speed bump.
 *
 * Purpose-built rather than reusing `AlertDialog`, which offers a single confirm
 * action. Both outcomes here are legitimate and neither is safe to assume:
 * cascading discards configuration the user may want, and splicing silently
 * changes where the survivors sit in the world.
 */
export function DeleteEntityDialog({
  open,
  entityId,
  descendants,
  promotedParent,
  onClose,
  onConfirm,
}: DeleteEntityDialogProps) {
  const childCount = descendants.length;
  const promotion =
    promotedParent === undefined
      ? "become top-level entities"
      : `move under ${promotedParent}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Required for any dialog inside the visualization workspace: the board's
      // drag sensor (`DndContainer/PointerSensor`) walks ancestors looking for
      // this attribute, and without it the sensor swallows clicks in here.
      data-not-draggable="true"
      maxWidth="sm"
    >
      <DialogTitle>Delete {entityId}?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {childCount === 1
            ? `${descendants[0]} is nested under ${entityId}.`
            : `${childCount} entities are nested under ${entityId}.`}{" "}
          Deleting only {entityId} keeps them, and they will {promotion}.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="outlined" onClick={() => onConfirm(false)}>
          Delete only {entityId}
        </Button>
        <Button variant="contained" onClick={() => onConfirm(true)}>
          Delete {childCount === 1 ? "both" : "all"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
